import React, { useEffect, useState } from "react";
import { showSuccess, showError } from "../../../utils/toastUtils";
import { useLoader } from "../../../utils/LoaderUtils";
import SMS_PROVIDERS from "../../../constants/smsConstants";
import smsAPI from "../../../api/notification/smsApi"; // FIX 1: Corrected API import

function SmsSettings() {
  const { showLoader, hideLoader } = useLoader();
  const [errors, setErrors] = useState({});
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [smsGateways, setSmsGateways] = useState({});

  const fetchGatewayDetails = async () => {
    try {
      const res = await smsAPI.getSmsGateways();

      if (res.code === 200) {
        const formatted = {};
        const providers = res.data?.providers || [];

        providers.forEach((g) => {
          formatted[g.providerName] = {
            _id: g._id,
            enabled: g.enabled,
            isDefault: g.isDefault || false,
            priority: g.priority || 1,
            config: g.config || {},
            fromIdentity: g.fromIdentity || {},
          };
        });

        setSmsGateways(formatted);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGatewayDetails();
  }, []);

  const handleToggle = (providerName) => {
    setSmsGateways((prev) => ({
      ...prev,
      [providerName]: {
        ...(prev[providerName] || {}),
        enabled: !(prev[providerName]?.enabled || false),
        isDefault: prev[providerName]?.isDefault || false,
        priority: prev[providerName]?.priority || 1,
        config: prev[providerName]?.config || {},
      },
    }));
  };

  const handleFieldChange = (provider, field, value) => {
    setSmsGateways((prev) => ({
      ...prev,
      [provider]: {
        ...(prev[provider] || {}),
        config: {
          ...(prev[provider]?.config || {}),
          [field]: value,
        },
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [`${provider}_${field}`]: "",
    }));
  };

  const validateProvider = (providerName) => {
    const newErrors = {};
    const meta = SMS_PROVIDERS[providerName];
    const g = smsGateways[providerName];

    if (!g?.enabled) return true;

    meta.fields.forEach((field) => {
      if (field.required && !g.config?.[field.name]) {
        newErrors[`${providerName}_${field.name}`] =
          `${field.label} is required`;
      }
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (providerName, g) => {
    const meta = SMS_PROVIDERS[providerName];
    const configData = g.config || {};

    const fromIdentity = {
      senderId: configData.senderId || null,
      fromNumber: configData.fromNumber || null,
    };

    return {
      providerName: providerName.toLowerCase(),
      displayName: meta.label,
      priority: g.priority || 1,
      enabled: g.enabled,
      isDefault: g.isDefault || false,
      fromIdentity,
      config: configData,
    };
  };

  const handleProviderSubmit = async (providerName) => {
    setErrors({});
    const gateway = smsGateways[providerName];

    if (!validateProvider(providerName)) return;

    try {
      setLoadingProvider(providerName);
      showLoader();

      const payload = buildPayload(providerName, gateway);
      let res;

      if (gateway._id) {
        res = await smsAPI.updateSmsProvider(gateway._id, payload);
      } else {
        res = await smsAPI.createSmsProvider(payload);
      }

      if (res.code === 200 || res.status === "success") {
        showSuccess(`${SMS_PROVIDERS[providerName].label} saved successfully`);
        fetchGatewayDetails();
      } else {
        showError(res.message || "Failed to save configuration");
      }
    } catch (err) {
      showError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoadingProvider(null);
      hideLoader();
    }
  };

  const handleDeleteProvider = async (providerName) => {
    const gateway = smsGateways[providerName];

    if (!gateway?._id) {
      setSmsGateways((prev) => ({
        ...prev,
        [providerName]: {
          ...prev[providerName],
          enabled: false,
        },
      }));
      return;
    }

    if (
      !window.confirm(
        `Delete ${SMS_PROVIDERS[providerName].label} configuration?`,
      )
    )
      return;

    try {
      showLoader();
      const res = await smsAPI.deleteSmsProvider(gateway._id);

      if (res.code === 200 || res.status === "success") {
        showSuccess(`${SMS_PROVIDERS[providerName].label} deleted`);
        fetchGatewayDetails();
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.response?.data?.message || "Delete failed");
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>SMS Gateway Settings</h4>
      </div>
      <div className="card-body">
        {Object.entries(SMS_PROVIDERS).map(([providerName, meta]) => {
          const gateway = smsGateways[providerName] || {};
          const isSaving = loadingProvider === providerName;

          return (
            <div className="row mb-3" key={providerName}>
              <div className="col-xl-12 d-flex">
                <div className="card flex-fill border w-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center">
                        <span className="system-app-icon">
                          <img src={meta.icon} alt={meta.label} />
                        </span>
                        <div className="security-title ms-2">
                          <h5 className="fs-16 fw-medium m-0">{meta.label}</h5>
                        </div>
                      </div>

                      <div className="status-toggle modal-status d-flex align-items-center gap-2">
                        {gateway?.isDefault && (
                          <span className="badge bg-success me-2">Default</span>
                        )}

                        <input
                          type="checkbox"
                          id={`${providerName}-toggle`}
                          className="check"
                          checked={!!gateway?.enabled}
                          onChange={() => handleToggle(providerName)}
                        />
                        <label
                          htmlFor={`${providerName}-toggle`}
                          className="checktoggle"
                        ></label>

                        {!gateway?.isDefault && gateway?._id && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => handleDeleteProvider(providerName)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>

                    <p className="fs-12 text-muted mb-2">{meta?.description}</p>

                    {gateway?.enabled && (
                      <div className="row shipment mt-3">
                        <div className="col-md-12 mb-3">
                          <div className="alert alert-light border fs-13 mb-0">
                            <strong>Setup Steps:</strong>
                            <ol className="ps-3 mb-0 mt-1">
                              {meta.setupSteps.map((step, index) => (
                                <li key={index}>{step}</li>
                              ))}
                            </ol>
                          </div>
                        </div>

                        {meta.fields.map((field) => (
                          <div className="col-md-6 mb-3" key={field.name}>
                            <label className="form-label fs-13">
                              {field.label}
                              {field.required && (
                                <span className="text-danger"> *</span>
                              )}
                            </label>

                            <input
                              type={field.type || "text"}
                              className="form-control"
                              placeholder={field.placeholder}
                              value={gateway.config?.[field.name] || ""}
                              onChange={(e) =>
                                handleFieldChange(
                                  providerName,
                                  field.name,
                                  e.target.value,
                                )
                              }
                            />

                            {errors[`${providerName}_${field.name}`] && (
                              <div className="errors text-danger fs-12 mt-1">
                                {errors[`${providerName}_${field.name}`]}
                              </div>
                            )}
                          </div>
                        ))}

                        <div className="mt-2 d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isSaving}
                            onClick={() => handleProviderSubmit(providerName)}
                          >
                            {isSaving
                              ? "Saving..."
                              : gateway._id
                                ? "Update Settings"
                                : "Save Settings"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default SmsSettings;
